import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from './api';

export interface PartAttachment {
  id: number;
  part_id: number;
  kind: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  sort_order: number;
  created_at: string;
  file_url: string;
}

type AttachmentKind = 'image' | 'document';

const listKey = (
  slug: string | undefined,
  partId: number | null,
  kind: AttachmentKind | undefined,
) => ['attachments', slug, partId, kind ?? 'all'] as const;

export function useAttachments(
  orgSlug: string | undefined,
  partId: number | null,
  kind?: AttachmentKind,
) {
  return useQuery<PartAttachment[]>({
    queryKey: listKey(orgSlug, partId, kind),
    queryFn: () => {
      const params = kind ? `?kind=${kind}` : '';
      return api.get<PartAttachment[]>(
        `/api/orgs/${orgSlug}/parts/${partId}/attachments${params}`,
      );
    },
    enabled: Boolean(orgSlug && partId),
  });
}

async function uploadAttachment(
  orgSlug: string,
  partId: number,
  file: File,
  kind: AttachmentKind,
): Promise<PartAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  // Documents go to a sibling endpoint so the server can apply the
  // PDF-specific validator + size cap; images stay on the bare path.
  const path =
    kind === 'document'
      ? `/api/orgs/${orgSlug}/parts/${partId}/attachments/documents`
      : `/api/orgs/${orgSlug}/parts/${partId}/attachments`;
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!response.ok) {
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(detail, response.status, body);
  }
  return body as PartAttachment;
}

export function useUploadAttachment(
  orgSlug: string | undefined,
  partId: number | null,
  kind: AttachmentKind = 'image',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      if (!orgSlug || !partId) throw new Error('missing context');
      return uploadAttachment(orgSlug, partId, file, kind);
    },
    onSuccess: () => {
      // Both kind-specific and the unfiltered cache need refreshing
      // since either could be displaying this attachment.
      qc.invalidateQueries({ queryKey: ['attachments', orgSlug, partId] });
    },
  });
}

export function useDeleteAttachment(
  orgSlug: string | undefined,
  partId: number | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: number) => {
      if (!orgSlug || !partId) throw new Error('missing context');
      return api.delete<unknown>(
        `/api/orgs/${orgSlug}/parts/${partId}/attachments/${attachmentId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attachments', orgSlug, partId] });
    },
  });
}
